import clsx from 'clsx';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import React, { memo } from 'react';
import { useLocation, useHistory } from 'react-router';

interface PaginationProps {
  totalPages: number;
  onPageChange?: (page: number) => void;
}

const Pagination: React.FC<PaginationProps> = memo(
  ({ totalPages, onPageChange }) => {
    if (totalPages === 0) return null;
    const location = useLocation();
    const history = useHistory();
    const currentPage = React.useMemo(() => {
      const search = new URLSearchParams(location.search);
      const page = search.get('page');
      const pageNumber = Number(page);

      return pageNumber || 1;
    }, [location.search]);

    const createPageArray = () => {
      const pages: (number | string)[] = [];

      if (totalPages <= 5) {
        for (let i = 1; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);

        if (currentPage > 4) pages.push('...');

        const start = Math.max(2, currentPage - 1);
        const end = Math.min(totalPages - 1, currentPage + 1);

        for (let i = start; i <= end; i++) pages.push(i);

        if (currentPage < totalPages - 3) pages.push('...');

        pages.push(totalPages);
      }

      return pages;
    };

    const onChange = (target: number) => {
      history.push(`${location.pathname}?page=${target}`);
      if (onPageChange) onPageChange(target);
    };

    const iconStyle = clsx(
      'p-0 text-primary disabled:opacity-50 [&>svg]:size-4 rounded-full p-1 border border-primary'
    );

    return (
      <div className="flex items-center space-x-2 mt-4 stroke-[2px]">
        <button
          className={iconStyle}
          onClick={() => onChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          <ArrowLeft />
        </button>

        {createPageArray().map((item, index) =>
          item === '...' ? (
            <span key={index} className="px-2">
              ...
            </span>
          ) : (
            <button
              key={index}
              className={`px-2 py-1 text-xs border rounded-full ${
                currentPage === item
                  ? 'bg-primary text-white'
                  : 'hover:bg-gray-200'
              }`}
              onClick={() => onChange(item as number)}
            >
              {item}
            </button>
          )
        )}

        <button
          className={iconStyle}
          onClick={() => onChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          <ArrowRight />
        </button>
      </div>
    );
  }
);

export default Pagination;
